import CloseIcon from 'assets/svg/app/close.svg'
import ExchangeIcon from 'assets/svg/app/exchange.svg'
import Button from 'components/Button'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { GridDivCenteredRow } from 'components/layout/grid'
import { providerChainIcon } from 'constants/network'
import { type FC, memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import DepositCrossMargin from 'sections/futures/DepositWithdraw/DepositCrossMargin'
import WithdrawCrossMargin from 'sections/futures/DepositWithdraw/WithdrawCrossMargin'
import { DepositTab, WithdrawTab } from 'sections/futures/Trade/ManageIsolatedMarginModal'
import { SectionHeading } from 'sections/shared/components/SectionHeading'

import { setOpenModal } from 'state/app/reducer'

import { selectPerpsProvider } from 'state/futures/common/selectors'

import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import styled from 'styled-components'
import { resetButtonCSS } from 'styles/common'
import { providerIsCrossMargin } from 'utils/futures'

interface TransferMarginPanelProps {
	onDismiss(): void
}

const TransferMarginPanel: FC<TransferMarginPanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()

	return (
		<Container>
			<ContentContainer rowGap="30px">
				<FlexDivRowCentered>
					<SectionHeading title={t('account.transfer-section.title')} marginTop={0} />
					<DismissButton onClick={onDismiss}>
						<CloseIcon width={12} height={12} />
					</DismissButton>
				</FlexDivRowCentered>
				<TransferMargin />
			</ContentContainer>
		</Container>
	)
})

export const TransferMargin: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const isCrossMarginProvider = providerIsCrossMargin(perpsProvider)
	const [isDeposit, setIsDeposit] = useState(true)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const showWalletDrawer = useCallback(() => dispatch(setOpenModal('manage_wallet')), [dispatch])

	const handleSwapClick = useCallback(() => {
		setIsDeposit((prev) => !prev)
	}, [])

	return (
		<FlexDivCol>
			<FlexDivCol rowGap="30px">
				<FlexDivCol rowGap="30px">
					<AccountTransferContainer>
						<AccountContainer style={{ padding: '10px' }}>
							<FromToLable color="secondary">
								{t('futures.market.trade.margin.modal.transfer.from')}
							</FromToLable>
							<FlexDivRowCentered columnGap="5px">
								{providerChainIcon(perpsProvider, { width: 12, height: 12 })}
								<Body color="primary">
									{isDeposit
										? abstractionWalletEnabled
											? t('futures.market.trade.margin.modal.transfer.synthetix-account')
											: t('futures.market.trade.margin.modal.transfer.perps-v2-account')
										: t('futures.market.trade.margin.modal.transfer.futures-account')}
								</Body>
							</FlexDivRowCentered>
						</AccountContainer>
						<IconContainer onClick={handleSwapClick} rotationDegrees={isDeposit ? 180 : 0}>
							<ExchangeIcon width={16} height={16} />
						</IconContainer>
						<Divider height={1} divider />
						<AccountContainer style={{ padding: '10px' }}>
							<FromToLable color="secondary">
								{t('futures.market.trade.margin.modal.transfer.to')}
							</FromToLable>
							<FlexDivRowCentered columnGap="5px">
								{providerChainIcon(perpsProvider, { width: 12, height: 12 })}
								<Body color="primary">
									{isDeposit
										? t('futures.market.trade.margin.modal.transfer.futures-account')
										: abstractionWalletEnabled
											? t('futures.market.trade.margin.modal.transfer.synthetix-account')
											: t('futures.market.trade.margin.modal.transfer.perps-v2-account')}
								</Body>
							</FlexDivRowCentered>
						</AccountContainer>
					</AccountTransferContainer>
					<TransferContentWrapper>
						{isCrossMarginProvider ? (
							isDeposit ? (
								<DepositCrossMargin />
							) : (
								<WithdrawCrossMargin />
							)
						) : isDeposit ? (
							<DepositTab />
						) : (
							<WithdrawTab />
						)}
					</TransferContentWrapper>
				</FlexDivCol>
			</FlexDivCol>
			<Footer>
				<Button size="small" fullWidth onClick={showWalletDrawer}>
					{t('account.transfer-section.button.cancel')}
				</Button>
			</Footer>
		</FlexDivCol>
	)
})

const TransferContentWrapper = styled.div`
  button[data-testid="futures-market-trade-deposit-margin-button"],
  button[data-testid="futures-market-trade-deposit-withdraw-button"] {
    position: fixed !important;
    bottom: 15px !important;
    right: 15px !important;
    width: calc(50% - 15px) !important;
    z-index: 2 !important;
    height: 40px !important;
    font-size: 14px !important;
  }
`

const ContentContainer = styled(FlexDivCol)`
	padding: 20px;
`

const AccountContainer = styled(FlexDivRow)`
	height: 45px;
`

const FromToLable = styled(Body)`
	line-height: 25px;
`
const AccountTransferContainer = styled(FlexDivCol)`
    border: ${(props) => props.theme.colors.selectedTheme.border};
    border-radius: 8px;
    position: relative;
`

const IconContainer = styled.div<{ rotationDegrees: number }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(${(props) => props.rotationDegrees}deg);
    background-color: ${(props) => props.theme.colors.selectedTheme.background};
    border: ${(props) => props.theme.colors.selectedTheme.border};
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    cursor: pointer;
    transition: transform 0.3s ease;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const Footer = styled(GridDivCenteredRow)`
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	column-gap: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 25px;
	right: 20px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

export default TransferMarginPanel
